import type { PowerupType, SnapshotDTO, TankDTO } from "../../shared/protocol/index.js";
import type { Renderer } from "./Renderer.js";
import type { FogView } from "./types.js";
import { isVisible } from "./fog.js";
import { pointSegDist2 } from "./util.js";

/**
 * Line-of-sight scope: a dotted guide showing where a shot would travel from
 * a scoped tank, accounting for the tank's velocity (inherited by the round)
 * and wall bounces. Drawn for EVERY scoped tank — others can see who has it —
 * yet still computed purely client-side (the server only flags `scoped`), so
 * it adds no simulation cost.
 */
export function drawScope(rd: Renderer, interp: SnapshotDTO, localId: string, fog: FogView | null): void {
  if (!rd.maze) return;
  const { ctx } = rd;
  for (const t of interp.tanks) {
    if (!t.alive || !t.scoped) continue;
    if (fog && t.id !== localId && !isVisible(t, fog)) continue;
    const paths = scopePaths(rd, t, tankVelocity(rd, t.id));
    ctx.save();
    ctx.lineCap = "round";
    ctx.strokeStyle = t.color;
    ctx.fillStyle = t.color;
    ctx.globalAlpha = 0.85;
    ctx.setLineDash([5, 7]);
    ctx.lineWidth = 2;
    for (const path of paths) {
      if (path.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
      ctx.stroke();
    }
    // Solid impact pip at the end of each guide.
    ctx.setLineDash([]);
    for (const path of paths) {
      const end = path[path.length - 1];
      if (!end) continue;
      ctx.beginPath();
      ctx.arc(end.x, end.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // Explosive: show the blast radius at the detonation point.
    const end = paths[0]?.[paths[0].length - 1];
    if ((t.weaponCharges.explosive ?? 0) > 0 && end) {
      ctx.globalAlpha = 0.16;
      ctx.beginPath();
      ctx.arc(end.x, end.y, rd.scope.explosionRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.6;
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }
}

/**
 * One or more trajectory polylines for a tank's current shot, composed from
 * whatever weapon effects it holds — mirrors the server's recipe: sniper is
 * fast/straight/piercing (only when not homing/explosive), explosive stops at
 * the first wall (unless homing suppresses it), tracking uses its longer
 * lifetime/bounces, multishot fans, laser is a hitscan beam.
 */
function scopePaths(rd: Renderer, me: TankDTO, vel: { x: number; y: number }): Array<{ x: number; y: number }[]> {
  const a = me.turretAngle;
  const sc = rd.scope;
  // A round lives `lifetime` seconds, so it travels its real speed × lifetime.
  const reach = (vx: number, vy: number, lifetime: number) => Math.hypot(vx, vy) * lifetime;
  const muzzle = rd.tankR + rd.bulletR + 2;
  const has = (w: PowerupType) => (me.weaponCharges[w] ?? 0) > 0;

  if (has("laser")) {
    // Beam carrier: a hitscan ray that reflects, from just outside the hull.
    // Honors the fan (multishot → several beams); ignores the projectile axes.
    const n = has("multishot") ? Math.max(1, Math.round(sc.multiCount)) : 1;
    const spread = has("multishot") ? (sc.multiSpread * Math.PI) / 180 : 0;
    const beams: Array<{ x: number; y: number }[]> = [];
    for (let i = 0; i < n; i++) {
      const ang = n > 1 ? a - spread / 2 + (spread / (n - 1)) * i : a;
      const bx = me.x + Math.cos(ang) * (rd.tankR + 2);
      const by = me.y + Math.sin(ang) * (rd.tankR + 2);
      beams.push(walkPath(rd, bx, by, Math.cos(ang), Math.sin(ang), { range: sc.laserRange, bounces: Infinity }));
    }
    return beams;
  }

  const homing = has("tracking");
  const explosive = has("explosive");
  const sniper = has("sniper");
  const speed = sc.bulletSpeed * (sniper ? sc.sniperSpeedMult : 1);
  const life = homing ? sc.trackingLifetime : sc.bulletLifetime;
  const bounces = homing ? sc.trackingBounces : sc.bulletBounces;
  const pureSniper = sniper && !homing && !explosive;
  const stopOnWall = explosive && !homing;

  // Trajectory for one launch angle, composing the held per-bullet effects.
  const walkShot = (ang: number): { x: number; y: number }[] => {
    const mx = me.x + Math.cos(ang) * muzzle;
    const my = me.y + Math.sin(ang) * muzzle;
    if (pureSniper) {
      return walkPath(rd, mx, my, Math.cos(ang), Math.sin(ang), {
        range: speed * life,
        straight: true,
        pierce: sc.sniperWallPierce,
      });
    }
    // Sniper ignores tank momentum; others inherit it.
    const pvx = Math.cos(ang) * speed + (sniper ? 0 : vel.x);
    const pvy = Math.sin(ang) * speed + (sniper ? 0 : vel.y);
    const range = reach(pvx, pvy, life);
    return stopOnWall
      ? walkPath(rd, mx, my, pvx, pvy, { range, stopOnWall: true })
      : walkPath(rd, mx, my, pvx, pvy, { range, bounces });
  };

  if (has("multishot")) {
    const n = Math.max(1, Math.round(sc.multiCount));
    const fan = (sc.multiSpread * Math.PI) / 180;
    const stepA = n > 1 ? fan / (n - 1) : 0;
    const start = a - fan / 2;
    const paths: Array<{ x: number; y: number }[]> = [];
    for (let i = 0; i < n; i++) paths.push(walkShot(n > 1 ? start + stepA * i : a));
    return paths;
  }
  return [walkShot(a)];
}

/**
 * March a virtual round through the maze, mirroring the server's stepBullets.
 * `straight` (sniper) flies in a line, passing through up to `pierce` walls
 * before stopping; otherwise it reflects off walls up to `bounces` times, or
 * `stopOnWall` halts at the first wall (explosive). Returns polyline vertices.
 */
function walkPath(
  rd: Renderer,
  ox: number,
  oy: number,
  vx: number,
  vy: number,
  opts: { range: number; bounces?: number; stopOnWall?: boolean; straight?: boolean; pierce?: number }
): Array<{ x: number; y: number }> {
  const { range, bounces: maxBounces = 0, stopOnWall = false, straight = false, pierce = 0 } = opts;
  const pts = [{ x: ox, y: oy }];
  const maze = rd.maze;
  if (!maze) return pts;
  const step = Math.max(2.5, rd.bulletR * 0.8);
  let x = ox;
  let y = oy;
  let bounces = 0;
  let pierced = 0;
  let dist = 0;
  let guard = 0;
  let wasInWall = false;
  while (dist < range && guard++ < 4000) {
    const sp = Math.hypot(vx, vy) || 1;
    const nx = x + (vx / sp) * step;
    const ny = y + (vy / sp) * step;
    if (nx < 0 || ny < 0 || nx > maze.width || ny > maze.height) {
      pts.push({ x: Math.max(0, Math.min(maze.width, nx)), y: Math.max(0, Math.min(maze.height, ny)) });
      return pts;
    }
    if (straight) {
      const inWall = hitsWall(rd, nx, ny);
      if (inWall && !wasInWall) {
        if (pierced >= pierce) {
          pts.push({ x, y });
          return pts;
        }
        pierced++;
      }
      wasInWall = inWall;
      x = nx;
      y = ny;
      dist += step;
      continue;
    }
    let bounced = false;
    if (hitsWall(rd, nx, y)) {
      if (stopOnWall) {
        pts.push({ x, y });
        return pts;
      }
      vx = -vx;
      bounces++;
      bounced = true;
    } else {
      x = nx;
    }
    if (hitsWall(rd, x, ny)) {
      if (stopOnWall) {
        pts.push({ x, y });
        return pts;
      }
      vy = -vy;
      bounces++;
      bounced = true;
    } else {
      y = ny;
    }
    if (bounced) {
      pts.push({ x, y });
      if (bounces > maxBounces) return pts;
    }
    dist += step;
  }
  pts.push({ x, y });
  return pts;
}

/** Same circle-vs-wall test the server uses, against the local maze segments. */
function hitsWall(rd: Renderer, x: number, y: number): boolean {
  const maze = rd.maze;
  if (!maze) return false;
  const reach = rd.bulletR + maze.thickness / 2;
  const r2 = reach * reach;
  for (const w of maze.walls) {
    if (pointSegDist2(x, y, w.x1, w.y1, w.x2, w.y2) <= r2) return true;
  }
  return false;
}

/** Estimate a tank's current velocity (px/s) from the last two snapshots. */
function tankVelocity(rd: Renderer, id: string): { x: number; y: number } {
  if (rd.buffer.length < 2) return { x: 0, y: 0 };
  const b = rd.buffer[rd.buffer.length - 1];
  const a = rd.buffer[rd.buffer.length - 2];
  const tb = b.snap.tanks.find((t) => t.id === id);
  const ta = a.snap.tanks.find((t) => t.id === id);
  const dt = (b.recvAt - a.recvAt) / 1000;
  if (!ta || !tb || dt <= 0) return { x: 0, y: 0 };
  return { x: (tb.x - ta.x) / dt, y: (tb.y - ta.y) / dt };
}

/** Laser windup preview: short flickering stub(s) along the aim. Fans into
 *  several stubs when the tank also holds multishot, mirroring the fired volley's
 *  spread. Assumes the context is already rotated so the turret aims along +x;
 *  the caller sets the stroke style / alpha. */
export function drawWindupStubs(
  rd: Renderer,
  ctx: CanvasRenderingContext2D,
  r: number,
  charges: Partial<Record<PowerupType, number>>
): void {
  const hasMulti = (charges.multishot ?? 0) > 0;
  const n = hasMulti ? Math.max(1, Math.round(rd.scope.multiCount)) : 1;
  const spread = hasMulti ? (rd.scope.multiSpread * Math.PI) / 180 : 0;
  for (let i = 0; i < n; i++) {
    const off = n > 1 ? -spread / 2 + (spread / (n - 1)) * i : 0;
    const c = Math.cos(off);
    const s = Math.sin(off);
    ctx.beginPath();
    ctx.moveTo(c * (r + 4), s * (r + 4));
    ctx.lineTo(c * (r + 54), s * (r + 54));
    ctx.stroke();
  }
}
